<template>
    <div>
        <!-- Booking case -->
        <div class="booking-case" ref="bookingCase">
            <div class="filterbar">
                <div class="d-flex flex-row">
                    <!-- Persons picker -->
                    <button type="button" class="btn btn-persons btn-block" @click="togglePersons">
                        <!-- Icon -->
                        <i class="icon far fa-user-friends"></i>

                        <span class="persons-text">
                            {{ adults + children.length }} {{ 'checkout.steps.calendar.guests' | choice(adults + children.length) }}
                        </span>
                    </button>
                </div>

                <!-- Travel period -->
                <button type="button" class="btn btn-travel-period btn-lg btn-block mt-2" @click="toggleCalendar">
                    <span class="placeholder text-primary" v-if="period === null">
                        {{ 'tripPage.propertyBookingCase.selectTravelPeriod' | trans }}
                    </span>

                    <span class="date-text" v-if="period !== null && period.from">
                        {{ period.from.format('DD.MM.') }}
                    </span>

                    <i class="far fa-arrow-right mx-1" v-if="period !== null && period.from && period.to"></i>

                    <span class="date-text" v-if="period !== null && period.to">
                        {{ period.to.format('DD.MM.') }}
                    </span>
                </button>
            </div>

            <!-- Calendar -->
            <div class="period-picker mt-2" v-show="isShowCalendar">
                <input :id="prefix" />
                <div :class="['periodCalendar', prefix]"></div>

                <button type="button" class="btn btn-link text-primary btn-block mb-3 mt-n1" @click="toggleCalendar">
                    {{ 'checkout.steps.calendar.personsCancel' | trans }}
                </button>
            </div>

            <!-- Persons -->
            <div class="persons py-3" v-show="isShowPersons">
                <!-- Adults -->
                <div class="adults">
                    <label>
                        <strong>
                            {{ 'nav.filterbar.adults' | trans }}
                        </strong>

                        <small class="d-block">
                            {{ 'nav.filterbar.adultsInfo' | trans }}
                        </small>
                    </label>

                    <div class="float-right">
                        <button type="button" class="btn btn-default" @click="adults = (adults > 1 ? adults - 1 : 1)">
                            <i class="fa fa-minus"></i>
                        </button>

                        <strong class="mx-2">{{ adults }}</strong>

                        <button type="button" class="btn btn-default" @click="adults++">
                            <i class="fa fa-plus"></i>
                        </button>
                    </div>
                </div>

                <div class="divider"></div>

                <!-- Children -->
                <div class="children">
                    <label>
                        <strong>
                            {{ 'nav.filterbar.children' | trans }}
                        </strong>

                        <small class="d-block">
                            {{ 'nav.filterbar.childrenInfo' | trans }}
                        </small>
                    </label>

                    <div class="float-right">
                        <button type="button" class="btn btn-default" @click="decreaseChildren()">
                            <i class="fa fa-minus"></i>
                        </button>

                        <strong class="mx-2">{{ children.length }}</strong>

                        <button type="button" class="btn btn-default" @click="increaseChildren()">
                            <i class="fa fa-plus"></i>
                        </button>
                    </div>

                    <!-- Children ages -->
                    <div class="children-age" v-for="(child, index) in children" :class="{ first : index === 0 }" :key="child.id">
                        <div class="birthdate">
                            <div class="input">
                                <input type="number" class="day" min="0" v-model="child.birthdate.day" :placeholder="'checkout.steps.calendar.childrenBirthdateDD' | trans" @keyup="jumpEnterBirthdate" @keydown="validateEnterBirthdate" @focus="clearBirthdateInput($event);child.birthdate.day = null;">
                                <input type="number" class="month" min="0" v-model="child.birthdate.month" :placeholder="'checkout.steps.calendar.childrenBirthdateMM' | trans" @keyup="jumpEnterBirthdate" @keydown="validateEnterBirthdate" @focus="clearBirthdateInput($event);child.birthdate.month = null;">
                                <input type="number" class="year" min="1940" v-model="child.birthdate.year" :placeholder="'checkout.steps.calendar.childrenBirthdateYYYY' | trans" @keyup="jumpEnterBirthdate" @keydown="validateEnterBirthdate" @focus="clearBirthdateInput($event);child.birthdate.year = null;">
                            </div>

                            <small class="age" v-if="(child.birthdate.day !== null && child.birthdate.day.length > 0) && (child.birthdate.month !== null && child.birthdate.month.length > 0) && (child.birthdate.year !== null && child.birthdate.year.length === 4)">
                                <span v-if="calculateChildAge(child) < 18">
                                    {{ calculateChildAge(child) }}
                                    {{ 'checkout.steps.calendar.years' | choice(calculateChildAge(child)) }}
                                </span>
                                <span class="text-danger" v-if="calculateChildAge(child) >= 18">
                                    {{ 'checkout.steps.calendar.childIsTooOld' | trans }}
                                </span>
                            </small>
                        </div>

                        <span class="d-flex flex-column">
                            <strong>
                                {{ 'checkout.steps.calendar.children' | choice(1) }} {{ index + 1 }}
                            </strong>

                            <small class="d-block">
                                {{ 'checkout.steps.calendar.childrenBirthdate' | trans }}
                            </small>
                        </span>
                    </div>
                </div>

                <button type="button" class="btn btn-primary btn-block btn-lg mt-3" @click="applyPersons()">
                    {{ 'checkout.steps.calendar.personsApply' | trans }}
                </button>

                <button type="button" class="btn btn-link text-primary btn-block" @click="togglePersons">
                    {{ 'checkout.steps.calendar.personsCancel' | trans }}
                </button>
            </div>

            <!-- Please select period -->
            <div class="d-flex flex-column align-items-center justify-content-center my-3" v-if="! period && ! isShowCalendar">
                <img :src="$cdn.default + 'illustrations/search.svg?raw=true'" class="d-inline-block w-50 mb-3" />

                <strong>
                    {{ 'tripPage.propertyBookingCase.selectPeriod' | trans }}
                </strong>

                <p class="text-muted mb-0 text-center">
                    {{ 'tripPage.propertyBookingCase.selectPeriodInfo' | trans }}
                </p>
            </div>

            <div v-show="! isShowCalendar && ! isShowPersons">
                <!-- Rooms -->
                <div v-if="rooms && rooms.length > 0 && period && period.to">
                    <span class="d-block text-center text-dark mb-1 font-weight-500 mt-3">
                        {{ 'tripPage.propertyBookingCase.rooms' | trans }}
                        ({{ rooms.map((room) => room.id).indexOf(currentRoomId) + 1 }}/{{ rooms.length }})
                    </span>

                    <!-- Horizontal room slider -->
                    <div class="horizontal-rooms-slider" v-if="rooms && rooms.length > 0">
                        <!-- Arrows -->
                        <button type="button" class="btn arrow-prev" @click="prevRoom" :disabled="rooms.map((room) => room.id).indexOf(currentRoomId) === 0">
                            <i class="fa fa-chevron-left"></i>
                        </button>
                        <button type="button" class="btn arrow-next" @click="nextRoom" :disabled="rooms.map((room) => room.id).indexOf(currentRoomId) === rooms.length - 1">
                            <i class="fa fa-chevron-right"></i>
                        </button>

                        <!-- Inner -->
                        <div class="inner">
                            <div class="room-item" v-for="room in rooms" v-show="room.id == currentRoomId" :key="'room_' + room.id">
                                <!-- Image -->
                                <img :src="'https://d36xws8ymuqmas.cloudfront.net/' + room.image + '?width=600&height=400'" :alt="room.name" style="width: 100%;">

                                <!-- Name -->
                                <span class="name">
                                    {{ room.name }}
                                </span>

                                <!-- Amenities -->
                                <div class="amenities">
                                    <!-- Bed description -->
                                    <span class="amenity-item" v-if="room.bed_description">
                                        <i class="wa-icon-single-bed mr-1"></i>
                                        {{ room.bed_description }}
                                    </span>

                                    <!-- Square meters -->
                                    <span class="amenity-item">
                                        <i class="wa-icon-surface mr-1"></i>
                                        {{ room.area_square_meters }}
                                        qm
                                    </span>

                                    <!-- Other amenities -->
                                    <span class="amenity-item" v-if="room.amenities" v-for="amenity in room.amenities.filter((amenity) => amenity.is_highlight || isShowAmenities).slice(0, isShowAmenities ? 999 : 5)">
                                        <i class="wa-icon-check mr-1" v-if="! amenity.icon"></i>
                                        <i class="mr-1" :class="[amenity.icon]" v-if="amenity.icon"></i>
                                        {{ amenity.name }}
                                    </span>
                                </div>

                                <!-- Toggle amenities -->
                                <button type="button" class="btn btn-link btn-sm text-left px-0" @click="toggleAmenities" v-show="room.amenities.filter((amenity) => amenity.is_highlight).length < room.amenities.length">
                                    <i class="fa fa-plus mr-1" v-if="! isShowAmenities"></i>
                                    <i class="fa fa-minus mr-1" v-if="isShowAmenities"></i>
                                    {{ 'tripPage.propertyBookingCase.showAmenities' | trans }}
                                </button>
                            </div>
                        </div>
                    </div>

                    <!-- Rooms -->
                    <div class="list-group list-group-flush" v-show="rooms.length > 0 && isShowRooms">
                        <a href="#" class="list-group-item list-group-item-action" :class="{ active: currentRoomId === room.id }" v-for="room in rooms" @click.prevent="selectRoom(room)">
                            <div class="d-flex w-100 justify-content-between">
                                <div class="d-flex flex-grow-1">
                                    <img :src="'https://d36xws8ymuqmas.cloudfront.net/' + room.image + '?width=600&height=400'" alt="..." style="flex-shrink: 0; width: 100%;">

                                    {{ room.name }}
                                </div>

                                <strong>
                                    {{ room.price | formatPriceRounded }}
                                    &euro;
                                </strong>
                            </div>
                        </a>
                    </div>
                </div>

                <!-- Loader -->
                <div class="text-center py-3" v-if="isLoading && period && period.to">
                    <svg class="loader" viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg">
                        <circle class="internal-circle" cx="60" cy="60" r="30"></circle>
                        <circle class="external-circle" cx="60" cy="60" r="50"></circle>
                    </svg>
                </div>

                <!-- No rooms available -->
                <div class="d-flex flex-column align-items-center justify-content-center my-3" v-if="! isLoading && period && rooms && rooms.length === 0">
                    <img :src="$cdn.default + 'illustrations/search.svg?raw=true'" class="d-inline-block w-50 mb-3" />

                    <strong>
                        {{ 'tripPage.propertyBookingCase.noRoomsFoundTitle' | trans }}
                    </strong>

                    <p class="text-muted mb-0 text-center">
                        {{ 'tripPage.propertyBookingCase.noRoomsFoundInfo' | trans }}
                    </p>
                </div>

                <div class="divider" v-if="! isLoading"></div>

                <!-- Refund options -->
                <div class="extras" v-if="! isLoading && refundOptions.length > 0">
                    <small class="d-block font-weight-700 text-primary mb-2">
                        {{ 'tripPage.propertyBookingCase.cancellationOptions' | trans }}
                    </small>
                    <div class="custom-control custom-radio" :key="'refund_option_radio_' + option.option_key" v-for="option in refundOptions">
                        <input class="custom-control-input" type="radio" :id="'refund_options_' + option.option_key + '_' + prefix" v-model="currentRefundOptionKey" :value="option.option_key">
                        <label class="custom-control-label w-100" :for="'refund_options_' + option.option_key + '_' + prefix">
                            <strong class="float-right" v-if="calculateDifference(option.price) != 0">
                                {{ calculateDifference(option.price) >= 0 ? '+' : '' }}
                                {{ calculateDifference(option.price) | formatPriceRounded }}
                                &euro;
                            </strong>

                            <span v-if="option.refundable_type === 'full'">
                                {{ 'checkout.summary.refundable' | trans }}
                            </span>
                            <span v-if="option.refundable_type === 'partial'">
                                {{ 'checkout.summary.partlyRefundable' | trans }}
                            </span>
                            <span v-if="option.refundable_type === 'none'">
                                {{ 'checkout.summary.nonRefundable' | trans }}
                            </span>

                            <!-- Refundable info -->
                            <small class="d-block font-weight-500 mt-n1" v-if="option.refundable_type">
                                <span class="text-primary" v-if="option.refundable_until">
                                    {{ 'checkout.summary.refundableUntil' | trans({ date: $moment(option.refundable_until).format('DD.MM.YYYY'), time: $moment(option.refundable_until).format('HH:mm') }) }}
                                </span>
                            </small>
                        </label>
                    </div>
                </div>
                
                <div class="divider" v-if="! isLoading"></div>

                <!-- Extras -->
                <div class="extras" v-if="! isLoading && extras.length > 0">
                    <small class="d-block font-weight-700 text-primary mb-2">
                        {{ 'tripPage.propertyBookingCase.extras' | trans }}
                    </small>
                    <div class="custom-control custom-radio" :key="'extra_radio_' + extra.key" v-for="extra in extras.filter(extra => extra.option_key === currentRefundOptionKey)">
                        <input class="custom-control-input" type="radio" :id="'extras_' + extra.key + '_' + prefix" v-model="currentRateKey" :value="extra.key">
                        <label class="custom-control-label w-100" :for="'extras_' + extra.key + '_' + prefix">
                            <strong class="float-right" v-if="calculateDifference(extra.price, extra.option_key) != 0">
                                {{ calculateDifference(extra.price, extra.option_key) >= 0 ? '+' : '' }}
                                {{ calculateDifference(extra.price, extra.option_key) | formatPriceRounded }}
                                &euro;
                            </strong>

                            <span v-if="extra.name">
                                {{ extra.name }}
                            </span>
                            <span v-if="! extra.name">
                                {{ 'tripPage.propertyBookingCase.withoutExtras' | trans }}
                            </span>
                        </label>
                    </div>
                </div>

                <div class="price-infos" v-if="currentRate && currentRate.fees && ! isLoading">
                    <!-- Total -->
                    <span class="total">
                        <span>
                            {{ 'tripPage.propertyBookingCase.total' | trans }}

                            <small class="d-block text-muted" v-if="currentRate.strikethrough_description">
                                {{ currentRate.strikethrough_description }}
                            </small>
                        </span>

                        <strong class="ml-auto">
                            {{ currentRate.price | formatPriceRounded }}
                            &euro;
                        </strong>
                    </span>

                    <!-- Fees info -->
                    <span v-for="feeInfo in currentRate.fees">
                        {{ 'search.fees.calculationTypes.exclusive_short' | trans }}
                        {{ feeInfo.total | formatPriceRounded }}
                        &euro;
                        {{ 'search.fees.types.' + feeInfo.type | trans }}
                    </span>
                </div>

                <!-- Profit -->
                <span class="profit-badge mt-2" v-if="currentRate && ! isLoading && currentRate.profit" v-popover="{ name: 'profit_' + currentRoomId }">
                    <i class="fa fa-coins mr-1"></i>
                    {{ currentRate.profit | formatPrice }}
                    &euro;
                    {{ 'search.profit' | trans }}

                    <!-- Popover -->
                    <popover :name="'profit_' + currentRoomId.id" :width="200" event="hover">
                        {{ 'search.profitInfo' | trans({ profit: currentRate.profit }) }}
                    </popover>
                </span>

                <!-- Sale scenario: package -->
                <span class="sale-scenario-badge sale-scenario-badge--package mt-2" v-if="! isLoading && currentRate && currentRate.sale_scenarios && currentRate.sale_scenarios.indexOf('package') !== -1" v-popover="{ name: 'package_info_' + currentRoomId }">
                    <i class="fa fa-cubes mr-1"></i>
                    {{ 'search.packageInfoLabel' | trans }}

                    <!-- Popover -->
                    <popover :name="'package_info_' + currentRoomId" :width="200" event="hover">
                        {{ 'search.packageInfo' | trans }}
                    </popover>
                </span>

                <!-- sale scenario: member -->
                <span class="sale-scenario-badge sale-scenario-badge--member text-center" v-if="! isLoading && currentRate && currentRate.sale_scenarios && currentRate.sale_scenarios.indexOf('member') !== -1 && isMember === true && currentRate.strikethrough_description" v-popover="{ name: 'member_info_' + currentRoomId }">
                    <i class="fa fa-badge-percent mr-1"></i>
                    {{ 'search.memberInfoLabel' | trans }}

                    <!-- Popover -->
                    <popover :name="'member_info_' + currentRoomId" :width="200" event="hover">
                        {{ 'search.memberInfo' | trans }}
                    </popover>
                </span>

                <!-- Book button -->
                <div class="book-wrapper" v-if="period && period.to">
                    <a :href="currentRate ? ($window.route('frontend.checkout.show') + '?' + query) : ''" class="btn btn-lg btn-primary book" rel="nofollow" :disabled="isLoading || ! currentRate">
                        <i class="wa-icon-long-arrow-right"></i>
                        {{ 'tripPage.propertyBookingCase.reserveNow' | trans }}
                    </a>
                </div>
            </div>

            <!-- Select period button -->
            <div class="book-wrapper" v-if="! period || (period && ! period.to)">
                <a href="#" class="btn btn-lg btn-primary book" rel="nofollow" @click.prevent="toggleCalendar">
                    {{ 'tripPage.propertyBookingCase.selectDate' | trans }}
                </a>
            </div>
        </div>

        <div class="booking-case mt-3">
            <!-- Filters -->
            <div class="search-filters" v-if="period && period.to && (isPackageEnabled || isMemberEnabled)">
                    <span class="heading pt-3">
                        {{ 'search.filters.typeOfRate' | trans }}
                    </span>

                    <!-- Package -->
                    <div class="form-group d-flex" v-if="isPackageEnabled">
                        <span class="label d-flex flex-column">
                            <strong>
                                {{ 'search.filters.packageTitle' | trans }}
                            </strong>
                            <small class="subtitle">
                                {{ 'search.filters.packageSubtitle' | trans }}
                            </small>
                        </span>

                        <button type="button" class="btn btn-toggle btn-toggle-lg ml-auto" :class="{ active: isPackage }" @click="() => isPackage = ! isPackage" aria-pressed="false" autocomplete="off" id="packageRateToggler">
                            <div class="handle"></div>
                        </button>
                    </div>

                    <!-- Member -->
                    <div class="form-group d-flex">
                        <span class="label d-flex flex-column">
                            <strong>
                                {{ 'search.filters.memberTitle' | trans }}
                            </strong>
                            <small class="subtitle">
                                {{ 'search.filters.memberSubtitle' | trans }}
                            </small>
                        </span>

                        <button type="button" class="btn btn-toggle btn-toggle-lg ml-auto" :class="{ active: isMember }" @click="() => isMember = ! isMember" aria-pressed="false" autocomplete="off" id="memberRateToggler">
                            <div class="handle"></div>
                        </button>
                    </div>
            </div>
        </div>
    </div>
</template>

<script>
    import PersonsPicker from './search/PersonsPicker.vue'
    import Hammer from 'hammerjs'

    export default {
        props: ['tripId', 'propertyId', 'roomId', 'prefix', 'isPackageEnabled', 'isMemberEnabled', 'ecommerceCategory', 'ecommerceName', 'trackDisabled'],
        components: {
            PersonsPicker,
        },
        data() {
            return {
                isInit: true,
                isDetailImpressionTracked: false,
                hammer: null,
                lastLoadRooms: null,

                period: null,
                persons: [],
                isLoading: false,
                isRefundable: false,
                isPackage: false,
                isMember: false,
                rooms: [],
                currentRoomId: null,
                currentBasePrice: 0.0,
                currentRefundOptionKey: null,
                refundOptions: [],
                currentRateKey: null,
                extras: [],

                isShowCalendar: false,
                isShowRooms: false,
                isShowAmenities: false,
                isShowPersons: false,

                // Persons
                adults: 2,
                children: [],
                tmpAdults: 2,
                tmpChildren: [],

                // Cancel sources
                loadRoomsCancelSource: null,
            }
        },
        mounted() {
            if (! this.currentRoomId) {
                this.currentRoomId = this.roomId
            }

            // Detect swipe gestures
            if (this.$refs.bookingCase) {
                this.hammer = Hammer(this.$refs.bookingCase)
                this.hammer.get('swipe').set({
                    direction: Hammer.DIRECTION_HORIZONTAL,
                    velocity: 0.1,
                });
                this.hammer.on('swipeleft', () => this.nextRoom())
                this.hammer.on('swiperight', () => this.prevRoom())
            }

            this.mount()
        },
        computed: {
            currentRate() {
                const currentExtra = this.$lodash.find(this.extras, { key: this.currentRateKey })

                return currentExtra || null
            },
            query() {
                let data = {
                    trip: this.tripId,
                    accommodation_id: this.propertyId,
                    accommodation_room_id: this.currentRoomId,
                    accommodation_rate_key: this.currentRateKey,
                    step: 'confirmation',
                    mode: 'standalone',
                }

                // Travel period
                if (this.period && this.period.from && this.period.to) {
                    data['arrive_date'] = this.period.from.format('YYYY-MM-DD')
                    data['departure_date'] = this.period.to.format('YYYY-MM-DD')
                }

                // Package
                if (this.isPackage) {
                    data['package'] = 'true'
                }

                // Package
                if (this.isMember) {
                    data['member'] = 'true'
                }

                // Persons
                data['adults'] = this.$lodash.sumBy(this.persons.filter((person) => person.type === 'adult'), 'amount')
                if (this.persons.filter((person) => person.type === 'child').length > 0) {
                    data['children'] = this.persons.filter((person) => person.type === 'child').map((child) => child.birthdate).join(',')
                }

                return this.serialize(data)
            },
        },
        methods: {
            mount() {
                var self = this

                // Travel period
                $('input#' + this.prefix).dateRangePicker({
                    separator: ' to ',
                    inline: true,
                    container: $('div.' + this.prefix),
                    alwaysOpen: true,
                    stickyMonths: true,
                    startDate: new Date(),
                    language: 'de',
                    startOfWeek: 'monday',
                    showTopbar: false,
                    singleMonth: true,
                    customArrowPrevSymbol: '<button type="button" class="btn"><i class="fa fa-chevron-left"></i></button>',
                    customArrowNextSymbol: '<button type="button" class="btn"><i class="fa fa-chevron-right"></i></button>'
                })
                .bind('datepicker-first-date-selected', function(event, data) {
                    self.period = {
                        from: moment(data.date1),
                        to: null
                    }
                })
                .bind('datepicker-change', function(event, data) {
                    self.period = {
                        from: moment(data.date1),
                        to: moment(data.date2)
                    };

                    if (! self.isInit) {
                        self.loadRooms()
                    }
                    self.isShowCalendar = false
                });

                // Read query params
                let params = this.$route.query

                // Travel period
                if (params.date_from && params.date_to) {
                    this.period = {
                        from: moment(params.date_from),
                        to: moment(params.date_to),
                    }

                    $('input#' + this.prefix).data('dateRangePicker')
                    .setDateRange(params.date_from, params.date_to);
                }

                // Persons
                if (typeof params.adults !== 'undefined') {
                    this.adults = parseInt(params.adults, 10)
                }
                if (typeof params.children !== 'undefined' && String(params.children).length > 0) {
                    this.children = params.children.split(',').map((child, index) => ({
                        id: index,
                        birthdate: {
                            day: child.split('-')[2],
                            month: child.split('-')[1],
                            year: child.split('-')[0],
                        }
                    }))
                }
                this.buildPersons()

                // Filters
                if (typeof params.filters !== 'undefined') {
                    const filters = params.filters.split(',')

                    // Refundable
                    if (filters.indexOf('refundable') !== -1) {
                        // this.isRefundable = true
                    }

                    // None refundable
                    if (filters.indexOf('none_refundable') !== -1) {
                        // this.isRefundable = false
                    }

                    // Package
                    if (filters.indexOf('package') !== -1) {
                        this.isPackage = true
                    }

                    // Member
                    if (filters.indexOf('member') !== -1) {
                        this.isMember = true
                    }
                }

                this.isInit = false
                this.loadRooms()
            },

            loadRooms() {
                var self = this;

                // Reset current room
                this.currentRoomId = null

                // Prevent if no period was selected
                if (! (this.period && this.period.from && this.period.to)) {
                    return;
                }

                // Check last load rooms
                const params = {
                    trip_id: this.tripId,
                    persons: this.persons,
                    arrive_date: this.period.from.format('YYYY-MM-DD'),
                    departure_date: this.period.to.format('YYYY-MM-DD'),
                    accommodation_preselection_id: this.propertyId,
                    accommodation_refundable: this.isRefundable,
                    accommodation_package: this.isPackage,
                    accommodation_member: this.isMember,
                };

                // Prevent request if it's identical with the last one
                if (JSON.stringify(params) === this.lastLoadRooms) {
                    return;
                }

                // Remember last load rooms
                this.lastLoadRooms = JSON.stringify(params)

                this.isLoading = true

                // Cancel previous search
                if (this.loadRoomsCancelSource !== null) {
                    this.loadRoomsCancelSource.cancel()
                }

                // Create cancel token
                const CancelToken = axios.CancelToken
                this.loadRoomsCancelSource = CancelToken.source()

                axios.post(route('api.checkout.accommodation'), params, {
                    cancelToken: this.loadRoomsCancelSource.token,
                }).then(function (response) {
                    self.isLoading = false

                    // Set rooms
                    if (response.data.data.accommodations && response.data.data.accommodations.length === 1) {
                        // Put selected room to beginning
                        let roomsData = response.data.data.accommodations[0].rooms
                        self.rooms = roomsData
                        // const index = roomsData.map((room) => room.id).indexOf(self.currentRoomId)
                        // if (index !== -1 && self.currentRoomId !== null) {
                        //     self.rooms = [
                        //         roomsData.splice(index, 1)[0],
                        //         ...roomsData,
                        //     ]
                        // } else {
                        //     self.rooms = roomsData
                        // }

                        // Set initial room
                        if (self.rooms.length > 0) {
                            self.selectRoom(self.rooms[0])

                            if (! self.isDetailImpressionTracked) {
                                self.trackDetailImpression(self.rooms[0])
                                self.isDetailImpressionTracked = true;
                            }
                        }
                        // if (self.rooms.length > 0 && self.rooms.map((room) => room.id).indexOf(self.currentRoomId) === -1) {
                        //     self.selectRoom(self.rooms[0])
                        // }
                    }

                    // Reset rooms
                    if (
                        response.data.data.accommodations.length === 0 || (
                            response.data.data.accommodations &&
                            response.data.data.accommodations.length > 0 &&
                            response.data.data.accommodations[0].rooms &&
                            response.data.data.accommodations[0].rooms.length === 0
                        )
                    ) {
                        self.rooms = []
                    }

                }).catch(function (error) {
                    console.log(error);
                    self.isLoading = false
                });
            },

            selectRoom(room) {
                this.currentRoomId = room.id
            },

            serialize(obj, prefix) {
                var str = [], p;

                for (p in obj) {
                    if (obj.hasOwnProperty(p)) {
                        var k = prefix ? prefix + "[" + p + "]" : p,
                        v = obj[p];

                        str.push((v !== null && typeof v === "object") ?
                            serialize(v, k) :
                            encodeURIComponent(k) + "=" + encodeURIComponent(v));
                    }
                }
                return str.join("&");
            },

            toggleCalendar() {
                this.isShowPersons = false

                this.isShowCalendar = ! this.isShowCalendar
            },

            // Move rooms
            prevRoom() {
                const currentIndex = this.rooms.map((room) => room.id).indexOf(this.currentRoomId)

                if (currentIndex > 0) {
                    this.currentRoomId = this.rooms[currentIndex - 1].id
                }
            },
            nextRoom() {
                const currentIndex = this.rooms.map((room) => room.id).indexOf(this.currentRoomId)

                if (currentIndex < this.rooms.length - 1) {
                    this.currentRoomId = this.rooms[currentIndex + 1].id
                }
            },

            toggleAmenities() {
                this.isShowAmenities = ! this.isShowAmenities
            },

            // Persons
            togglePersons() {
                this.isShowCalendar = false

                // Save selection
                if (this.isShowPersons) {
                    this.adults = this.tmpAdults;
                    this.children = JSON.parse(JSON.stringify(this.tmpChildren));
                } else {
                    this.tmpAdults = this.adults;
                    this.tmpChildren = JSON.parse(JSON.stringify(this.children));
                }

                this.isShowPersons = ! this.isShowPersons
            },
            applyPersons() {
                this.tmpAdults = this.adults;
                this.tmpChildren = JSON.parse(JSON.stringify(this.children));

                this.isShowPersons = false

                this.buildPersons()
            },
            increaseChildren: function() {
                this.children.push({
                    id: this.children.length,
                    birthdate: {
                        day: null,
                        month: null,
                        year: null
                    }
                });
                setTimeout(function() {
                    $('div.calendar div.children-age').last().find('div.birthdate input.day').focus();
                }, 200)
            },
            decreaseChildren: function() {
                this.children.pop();
            },
            calculateChildAge: function(child) {
                return this.$moment().diff(this.getChildBirthdateString(child), 'years', false);
            },
            getChildBirthdateString: function(child) {
                var year = child.birthdate.year;
                var month = parseInt(child.birthdate.month, 10) < 10 ? '0' + parseInt(child.birthdate.month, 10) : '' + child.birthdate.month;
                var day = parseInt(child.birthdate.day, 10) < 10 ? '0' + parseInt(child.birthdate.day, 10) : '' + child.birthdate.day;
                var birthdateString = year + '-' + month + '-' + day;

                return birthdateString;
            },
            clearBirthdateInput: function(event) {
                event.target.value = '';
            },
            validateEnterBirthdate: function(event) {
                var value = event.target.value;
                var newValue = parseInt('' + event.target.value + event.key, 10);

                if (event.key === 'Backspace') return true;

                // Max length
                switch (event.target.className) {
                    case 'day':
                        if (value.length > 2 || newValue > 31) {
                            event.preventDefault();
                        } else if (value.length === 2) {
                            event.target.value = '';
                        }
                        break;

                    case 'month':
                        if (value.length > 2 || newValue > 12) {
                            event.preventDefault();
                        } else if (value.length === 2) {
                            event.target.value = '';
                        }
                        break;

                    case 'year':
                        if (value.length > 4) {
                            event.preventDefault();
                        } else if (value.length === 4) {
                            event.target.value = '';
                        }
                        break;
                }
            },
            jumpEnterBirthdate: function(event) {
                if (event.target.className !== 'year' && event.target.value.length === 2) {
                    event.target.nextElementSibling.focus();
                }
            },
            buildPersons: function() {
                var persons = [];
                persons.push({
                    type: 'adult',
                    birthdate: '1990-01-01',
                    amount: this.adults,
                });

                for (var i = 0; i < this.children.length; i++) {
                    var child = this.children[i];
                    persons.push({
                        type: 'child',
                        birthdate: this.getChildBirthdateString(child),
                        amount: 1
                    });
                }

                this.persons = persons;
                if (! this.isInit) {
                    this.loadRooms()
                }
            },

            trackDetailImpression(item) {
                if (this.trackDisabled) {
                    return;
                }

                // Clear previous ecommerce object
                this.$window.dataLayer.push({
                    ecommerce: null,
                })

                // Push updated data
                this.$window.dataLayer.push({
                    'event': 'eeDetailImpression',
                    'ecommerce': {
                        'currencyCode': 'EUR',
                        'detail': {
                            'products': [{
                                'id': 'watado-trips_' + this.propertyId,
                                'name': this.ecommerceName,
                                'price': item.price ? item.price : null,
                                'revenue': item.price ? item.price : null,
                                'tax': 0,
                                'shipping': 0,
                                'brand': 'watado-trips',
                                'category': this.ecommerceCategory,
                                'quantity': 1,
                                'affiliation': 'watado-trips',
                            }],
                        },
                    }
                })
            },

            calculateDifference(price, optionKey = null) {
                let basePrice = this.currentBasePrice;

                if (optionKey) {
                    basePrice = this.$lodash.find(this.refundOptions, {
                        option_key: optionKey,
                    }).price
                }

                return (price - basePrice) >= 1 ? (price - basePrice) : 0
            },
        },
        watch: {
            period: {
                handler(val) {
                    // Clone current query params
                    let queryParams = Object.assign({}, this.$route.query)

                    // Travel period
                    if (this.period && this.period.from && this.period.to) {
                        queryParams['date_from'] = this.period.from.format('YYYY-MM-DD')
                        queryParams['date_to'] = this.period.to.format('YYYY-MM-DD')
                    }

                    // Update current route
                    this.$router.push({
                        path: this.$route.path,
                        // name: this.$route.name,
                        // mode: 'history',
                        query: queryParams,
                    }).catch(err => {
                        console.log(err)
                        if (err.name !== 'NavigationDuplicated' && ! err.message.includes('Avoided redundant navigation to current location')) {
                            console.log(err);
                        }
                    });
                },
                deep: true
            },
            currentRoomId() {
                var self = this

                const room = this.$lodash.find(this.rooms, { id: this.currentRoomId })

                if (room && room.rates) {
                    this.extras = room.rates.map((rate) => ({
                        ...rate,
                        option_key: rate.refundable_type + '_' + rate.refundable_until,
                        key: rate.source_offer_id,
                        name: rate.amenities.map((amenity) => amenity.name).join(', '),
                        // amenities: rate.amenities.map((amenity) => amenity.id),
                    }))
                    let refundOptions = room.rates
                    .sort((rateA, rateB) => {
                        if (rateA.price < rateB.price) {
                            return -1;
                        }

                        if (rateA.price > rateB.price) {
                            return 1;
                        }

                        return 0;
                    })
                    .map((rate) => ({
                        ...rate,
                        option_key: rate.refundable_type + '_' + rate.refundable_until,
                    }))
                    this.refundOptions = this.$lodash.uniqBy(refundOptions, 'option_key')
                    this.currentBasePrice = this.$lodash.sortBy(refundOptions, ['price'])[0].price
                } else {
                    this.extras = []
                    this.refundOptions = []
                }

                // Set initial extras
                if (this.extras.length > 0) {
                    const currentRefundOptionKey = this.refundOptions[0].option_key  
                    this.currentRefundOptionKey = currentRefundOptionKey
                    this.currentRateKey = this.extras.filter(extra => {
                        return extra.option_key === currentRefundOptionKey
                    })[0].key
                }
            },
            isRefundable() {
                this.loadRooms()
            },
            isPackage() {
                this.loadRooms()
            },
            isMember() {
                this.loadRooms()
            },
            currentRefundOptionKey() {
                if (this.extras.length > 0 && this.refundOptions.length > 0) {
                    const currentRefundOptionKey = this.currentRefundOptionKey
                    this.currentRateKey = this.extras.filter(extra => {
                        return extra.option_key === currentRefundOptionKey
                    })[0].key
                }
            },
        }
    }
</script>
